import { uuid25 } from "../../../uuid/dist/uuid25.js";
import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { ColumnOption, ColumnType } from "../../../constants/schema.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { toPastedDomainId } from "../dictionary/membership.js";
import { addColumnAction, changeColumnAutoIncrementAction, changeColumnCommentAction, changeColumnDataTypeAction, changeColumnDefaultAction, changeColumnDomainAction, changeColumnLogicalNameAction, changeColumnNameAction, changeColumnNameDerivedAction, changeColumnNotNullAction, changeColumnUniqueAction } from "../table-column/atom.actions.js";
import { toDomainUnlinkActions } from "../dictionary/domainTypes.js";
import { bHas } from "../../../utils/bit.js";
import { SelectType } from "../editor/state.js";
import { isDomainInTypeCell } from "../editor/utils/nameView.js";
import { getTableGroupId } from "../../../utils/tableGroup.js";
import { nextPoint, nextZIndex } from "../../../utils/index.js";
import { isTableFocusType } from "../editor/utils/focus.js";
import { drawEndRelationshipAction, focusColumnAction, focusTableAction, focusTableEndAction, selectAction, unselectAllAction } from "../editor/atom.actions.js";
import { removeIndexAction } from "../index/atom.actions.js";
import { addRelationshipAction, removeRelationshipAction } from "../relationship/atom.actions.js";
import { ActionType } from "./actions.js";
import { addTableAction, changeTableNameAction, changeTableNameDerivedAction, changeZIndexAction, moveToTableAction, removeTableAction, tableReducers } from "./atom.actions.js";
import { resizeTableGroupAction } from "../table-group/atom.actions.js";
import { getStartKeyColumns, toForeignKeyActions } from "../relationship/fkColumns.js";
import { toColumnNameWrites } from "../dictionary/derivedNames.js";
import { getShowColumnOrder } from "../../../utils/table-clipboard/index.js";
import { drawStartAddRelationshipAction$ } from "../editor/generator.actions.js";
//#region src/engine/modules/table/generator.actions.ts
var addTableAction$ = () => function* ({ settings, doc: { tableIds, memoIds }, collections }) {
	const tables = query(collections).collection("tableEntities").selectByIds(tableIds);
	const memos = query(collections).collection("memoEntities").selectByIds(memoIds);
	const point = nextPoint(settings, tables, memos);
	const id = uuid25();
	yield unselectAllAction();
	yield addTableAction({
		id,
		ui: {
			...point,
			zIndex: nextZIndex(tables, memos)
		}
	});
	yield selectAction({ [id]: SelectType.table });
	yield focusTableAction({ tableId: id });
};
var removeTableAction$ = (id) => function* ({ doc: { tableIds, relationshipIds, indexIds }, editor: { selectedMap }, collections }) {
	const relationships = query(collections).collection("relationshipEntities").selectByIds(relationshipIds);
	const indexes = query(collections).collection("indexEntities").selectByIds(indexIds);
	if (id) {
		const removeRelationships = relationships.filter(({ start, end }) => start.tableId === id || end.tableId === id);
		const removeIndexes = indexes.filter(({ tableId }) => tableId === id);
		for (const { id } of removeIndexes) yield removeIndexAction({ id });
		for (const { id } of removeRelationships) yield removeRelationshipAction({ id });
		yield removeTableAction({ id });
		return;
	}
	const isInDoc = arrayHas(tableIds);
	const selectedTableIds = Object.entries(selectedMap).filter(([id, type]) => type === SelectType.table && isInDoc(id)).map(([id]) => id);
	const hasTableIds = arrayHas(selectedTableIds);
	const removeRelationships = relationships.filter(({ start, end }) => hasTableIds(start.tableId) || hasTableIds(end.tableId));
	const removeIndexes = indexes.filter(({ tableId }) => hasTableIds(tableId));
	for (const { id } of removeIndexes) yield removeIndexAction({ id });
	for (const { id } of removeRelationships) yield removeRelationshipAction({ id });
	for (const id of selectedTableIds) yield removeTableAction({ id });
};
var selectTableAction$ = (id, $mod) => function* ({ doc: { tableIds, memoIds, domainIds }, collections, editor: { drawRelationship }, settings: { database } }) {
	const tableCollection = query(collections).collection("tableEntities");
	const tables = tableCollection.selectByIds(tableIds);
	const memos = query(collections).collection("memoEntities").selectByIds(memoIds);
	if (!$mod) yield unselectAllAction();
	yield selectAction({ [id]: SelectType.table });
	yield changeZIndexAction({
		id,
		zIndex: nextZIndex(tables, memos)
	});
	yield focusTableAction({ tableId: id });
	if (!drawRelationship) return;
	if (drawRelationship.start) {
		const startTable = tableCollection.selectById(drawRelationship.start.tableId);
		const endTable = tableCollection.selectById(id);
		if (!startTable || !endTable) {
			yield drawEndRelationshipAction();
			return;
		}
		const columnCollection = query(collections).collection("tableColumnEntities");
		const startColumns = getStartKeyColumns(collections, startTable);
		if (!startColumns.length) {
			yield drawEndRelationshipAction();
			return;
		}
		const endColumnIds = startColumns.map(() => uuid25());
		yield toForeignKeyActions(startColumns, endTable.id, endColumnIds, {
			startTableName: startTable.name,
			endColumnNames: columnCollection.selectByIds(endTable.columnIds).map(({ name }) => name),
			database,
			domainIds
		});
		yield addRelationshipAction({
			id: uuid25(),
			relationshipType: drawRelationship.relationshipType,
			start: {
				tableId: startTable.id,
				columnIds: startColumns.map(({ id }) => id)
			},
			end: {
				tableId: endTable.id,
				columnIds: endColumnIds
			}
		});
		yield drawEndRelationshipAction();
	} else yield drawStartAddRelationshipAction$(id);
};
var EVERY_CELL = {
	name: true,
	logicalName: true,
	dataType: true,
	domain: true
};
function toPastedCells(settings, showColumnOrder) {
	const dataType = showColumnOrder.includes(ColumnType.columnDataType);
	return {
		name: showColumnOrder.includes(ColumnType.columnName),
		logicalName: showColumnOrder.includes(ColumnType.columnLogicalName),
		dataType,
		domain: showColumnOrder.includes(ColumnType.columnDomain) || dataType && isDomainInTypeCell(settings)
	};
}
function toPastedValueActions(payload, column, showColumnOrder) {
	return showColumnOrder.flatMap((columnType) => {
		switch (columnType) {
			case ColumnType.columnDefault: return [changeColumnDefaultAction({
				...payload,
				value: column.default
			})];
			case ColumnType.columnComment: return [changeColumnCommentAction({
				...payload,
				value: column.comment
			})];
			case ColumnType.columnAutoIncrement: return [changeColumnAutoIncrementAction({
				...payload,
				value: bHas(column.options, ColumnOption.autoIncrement)
			})];
			case ColumnType.columnUnique: return [changeColumnUniqueAction({
				...payload,
				value: bHas(column.options, ColumnOption.unique)
			})];
			case ColumnType.columnNotNull: return [changeColumnNotNullAction({
				...payload,
				value: bHas(column.options, ColumnOption.notNull)
			})];
			default: return [];
		}
	});
}
function toPastedNameDomainActions(state, payload, current, pasted, cells) {
	const actions = [];
	const wasDerived = current ? bHas(current.options, ColumnOption.nameDerived) : false;
	const domainTypeOf = (id) => state.collections.domainEntities[id]?.dataType ?? "";
	if (cells.name) {
		actions.push(changeColumnNameAction({
			...payload,
			value: pasted.name
		}));
		if (wasDerived) actions.push(changeColumnNameDerivedAction({
			...payload,
			value: false
		}));
	}
	let link = cells.domain ? toPastedDomainId(state, pasted.domainId, pasted.dataType) : null;
	const dataType = cells.dataType ? pasted.dataType : null;
	if (cells.logicalName) {
		actions.push(changeColumnLogicalNameAction({
			...payload,
			value: pasted.logicalName
		}));
		const left = {
			...pasted,
			id: payload.id,
			name: cells.name ? pasted.name : current?.name ?? "",
			options: !cells.name && wasDerived ? ColumnOption.nameDerived : 0
		};
		const writes = toColumnNameWrites(state, payload.tableId, left, pasted.logicalName);
		const termDomainId = writes.link?.value ?? "";
		actions.push(...writes.actions);
		if (termDomainId && !link && (!dataType || domainTypeOf(termDomainId) === dataType)) link = termDomainId;
	}
	if (link) {
		const domainType = domainTypeOf(link);
		if (current?.domainId !== link) actions.push(changeColumnDomainAction({
			...payload,
			value: link
		}));
		if (dataType !== null || current?.dataType !== domainType) actions.push(changeColumnDataTypeAction({
			...payload,
			value: domainType
		}));
		return actions;
	}
	if (link === "" && current?.domainId !== "") actions.push(changeColumnDomainAction({
		...payload,
		value: ""
	}));
	if (dataType !== null) {
		actions.push(changeColumnDataTypeAction({
			...payload,
			value: dataType
		}));
		if (link === null) actions.push(...toDomainUnlinkActions(state, [{
			...payload,
			value: dataType
		}]));
	}
	return actions;
}
var pasteTableAction$ = (columns) => function* (state) {
	const { editor: { selectedMap, focusTable }, settings, collections } = state;
	const isFit = focusTable && !isTableFocusType(focusTable.focusType);
	const selectedTableIds = Object.entries(selectedMap).filter(([, type]) => type === SelectType.table).map(([id]) => id).filter((id) => isFit ? id !== focusTable?.tableId : true);
	for (const tableId of selectedTableIds) for (const column of columns) {
		const payload = {
			id: uuid25(),
			tableId
		};
		yield [
			addColumnAction(payload),
			changeColumnDefaultAction({
				...payload,
				value: column.default
			}),
			changeColumnCommentAction({
				...payload,
				value: column.comment
			}),
			changeColumnNotNullAction({
				...payload,
				value: bHas(column.options, ColumnOption.notNull)
			}),
			changeColumnUniqueAction({
				...payload,
				value: bHas(column.options, ColumnOption.unique)
			}),
			changeColumnAutoIncrementAction({
				...payload,
				value: bHas(column.options, ColumnOption.autoIncrement)
			}),
			...toPastedNameDomainActions(state, payload, void 0, column, EVERY_CELL)
		];
	}
	if (!focusTable || !isFit) return;
	const table = query(collections).collection("tableEntities").selectById(focusTable.tableId);
	if (!table) return;
	const columnIds = table.columnIds.filter(arrayHas(focusTable.selectColumnIds));
	if (columnIds.length === 0) return;
	const additionalColumnIds = table.columnIds.slice(table.columnIds.indexOf(columnIds[columnIds.length - 1]) + 1);
	const columnIdsRange = [...columnIds, ...additionalColumnIds];
	const showColumnOrder = getShowColumnOrder(settings.show, settings.columnOrder, settings.nameViewMode);
	const cells = toPastedCells(settings, showColumnOrder);
	const columnCollection = query(collections).collection("tableColumnEntities");
	yield focusTableEndAction();
	for (let i = 0; i < columnIdsRange.length; i++) {
		const columnId = columnIdsRange[i];
		const column = columns[i];
		if (!column) break;
		const payload = {
			id: columnId,
			tableId: table.id
		};
		yield [
			...toPastedValueActions(payload, column, showColumnOrder),
			...toPastedNameDomainActions(state, payload, columnCollection.selectById(columnId), column, cells),
			focusColumnAction({
				tableId: table.id,
				columnId,
				focusType: focusTable.focusType,
				$mod: true,
				shiftKey: false
			})
		];
	}
	const addColumns = columns.slice(columnIdsRange.length);
	for (const column of addColumns) {
		const id = uuid25();
		const payload = {
			id,
			tableId: table.id
		};
		yield [
			addColumnAction(payload),
			...toPastedValueActions(payload, column, showColumnOrder),
			...toPastedNameDomainActions(state, payload, void 0, column, cells),
			focusColumnAction({
				tableId: table.id,
				columnId: id,
				focusType: focusTable.focusType,
				$mod: true,
				shiftKey: false
			})
		];
	}
};
var sortTablesToMoveAction$ = () => function* (state, context) {
	const { doc, collections } = state;
	const copies = query(collections).collection("tableEntities").selectByIds(doc.tableIds).map((table) => ({
		...table,
		ui: { ...table.ui }
	}));
	const groups = query(collections).collection("tableGroupEntities").selectByIds(doc.tableGroupIds).map((group) => ({
		...group,
		ui: { ...group.ui }
	}));
	const sorted = {
		...state,
		collections: {
			...collections,
			tableEntities: Object.fromEntries(copies.map((table) => [table.id, table])),
			tableGroupEntities: Object.fromEntries(groups.map((group) => [group.id, group]))
		}
	};
	tableReducers[ActionType.sortTable](sorted, {
		type: ActionType.sortTable,
		payload: void 0
	}, context);
	const placedGroupIds = new Set(copies.map((table) => getTableGroupId(sorted, table)));
	yield copies.map(({ id, ui: { x, y } }) => moveToTableAction({
		id,
		x,
		y
	}));
	yield groups.filter(({ id }) => placedGroupIds.has(id)).map(({ id, ui: { x, y, width, height } }) => resizeTableGroupAction({
		id,
		x,
		y,
		width,
		height
	}));
};
var changeTableNameAction$ = (id, value) => function* ({ collections }) {
	const table = query(collections).collection("tableEntities").selectById(id);
	yield [changeTableNameAction({
		id,
		value
	}), ...table?.nameDerived ? [changeTableNameDerivedAction({
		id,
		value: false
	})] : []];
};
var actions$ = {
	addTableAction$,
	removeTableAction$,
	selectTableAction$,
	pasteTableAction$,
	sortTablesToMoveAction$,
	changeTableNameAction$
};
//#endregion
export { actions$, addTableAction$, changeTableNameAction$, pasteTableAction$, removeTableAction$, selectTableAction$, sortTablesToMoveAction$ };
